import React, { useEffect, useLayoutEffect, useState } from 'react';
import { Arbeitsschritt } from './types';
import { supabase } from '../../services/supabaseClient';
import { useGefaehrdungsVorschlag } from './hooks/useGefaehrdungsVorschlag';
import { Bot, Plus, X, Save, Loader2, BookmarkPlus, ShieldCheck, BookOpen, FileText } from 'lucide-react';
import { Button } from '../Button';
import { MitgeltendeDokumenteEditor } from './MitgeltendeDokumenteEditor';
import {
  aendereDetail,
  listenAus,
  neuerDetailStand,
  offeneFelder,
  spaltenUebernehmen,
  speichereDetail,
  uebernimmVonAussen,
} from './unterlagenErgaenzen';
import type { DetailStand, DetailSpeicherErgebnis, Listen, SchrittDb } from './unterlagenErgaenzen';

// Mittelbereich: bearbeitet die PDF-relevanten Felder des gewählten Arbeitsschritts direkt
// (Schutzmaßnahmen, Vorschriften, mitgeltende Dokumente).
// KI-Vorschlag füllt Schutzmaßnahmen + Vorschriften; „In Katalog" speichert global für alle.
//
// Speichern läuft über die Schreib-Warteschlange je Schritt
// (`schrittSchreibWarteschlange.ts`, gemeinsam mit „Unterlagen ergänzen“ und
// dem Bearbeiten-Dialog der Liste): strikt nacheinander, und jeder Auftrag
// liest erst bei Ausführung die frische Zeile, führt sie mit den hier offenen
// Listen zusammen und schreibt NUR diese (`speichereDetail`).

/** Datenbankzugriff der Aufträge dieser Ansicht. */
const DETAIL_DB: SchrittDb = {
  lese: async (id) => {
    const { data, error } = await supabase.from('sige_arbeitsschritte').select('*').eq('id', id).maybeSingle();
    if (error) throw error;
    return (data as Arbeitsschritt | null) ?? null;
  },
  schreibe: async (id, felder) => {
    const { data, error } = await supabase.from('sige_arbeitsschritte').update(felder).eq('id', id).select().single();
    if (error) throw error;
    return (data as Arbeitsschritt | null) ?? null;
  },
};

interface SchrittDetailProps {
  schritt: Arbeitsschritt;
  onSchrittChange: (s: Arbeitsschritt) => void;
  /**
   * Meldet dem Editor eine Funktion, die einen noch ausstehenden
   * Auto-Speicher-Vorgang sofort schreibt (und wirft, wenn das misslingt).
   * Der Editor ruft sie vor einem Versionswechsel und vor dem Duplizieren —
   * sonst fehlte die Änderung der letzten 1,2 s in Datenbank und Kopie.
   */
  onRegisterFlush?: (flush: (() => Promise<void>) | null) => void;
}

const Liste: React.FC<{
  titel: string;
  icon: React.ReactNode;
  items: string[];
  placeholder: string;
  onChange: (items: string[]) => void;
}> = ({ titel, icon, items, placeholder, onChange }) => {
  const [draft, setDraft] = useState('');
  const add = () => {
    if (!draft.trim()) return;
    onChange([...items, draft.trim()]);
    setDraft('');
  };
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 mb-3">{icon}{titel}
        <span className="ml-auto text-xs font-normal text-slate-400">{items.length}</span>
      </div>
      <div className="space-y-1 mb-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2 text-sm bg-slate-50 border border-slate-200 rounded px-2 py-1">
            <span className="flex-1">{it}</span>
            <button onClick={() => onChange(items.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500 mt-0.5">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
        {items.length === 0 && <div className="text-xs text-slate-400 italic">Keine Einträge.</div>}
      </div>
      <div className="flex gap-2">
        <input
          className="flex-1 text-sm p-2 border border-slate-200 rounded outline-none focus:ring-2 focus:ring-blue-500/20"
          placeholder={placeholder}
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        />
        <Button variant="outline" size="sm" onClick={add}><Plus className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export const SchrittDetail: React.FC<SchrittDetailProps> = ({ schritt, onSchrittChange, onRegisterFlush }) => {
  const [listen, setListen] = useState<Listen>(() => listenAus(schritt));
  const massnahmen = listen.schutzmassnahmen;
  const vorschriften = listen.vorschriften;
  const dokumente = listen.mitgeltende_dokumente;
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [katalogStatus, setKatalogStatus] = useState<string | null>(null);
  const [meldung, setMeldungText] = useState<string | null>(null);
  const [meldungTon, setMeldungTon] = useState<'info' | 'fehler'>('info');
  // Eine Meldung trägt ihren Ton mit: Fehler dürfen nicht wie Erfolgsmeldungen aussehen.
  const setMeldung = (text: string | null, ton: 'info' | 'fehler' = 'info') => {
    setMeldungText(text);
    setMeldungTon(ton);
  };
  const { suggestHazards, loading: aiLoading } = useGefaehrdungsVorschlag();

  const saveTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  // Bearbeitungsstand des gezeigten Schritts (lokale Listen, Basis,
  // Revisionen). Je Schritt ein eigenes Objekt: ein noch laufender Auftrag
  // schreibt nach einem Wechsel weiter SEINEN Schritt mit dessen Listen.
  const standRef = React.useRef<DetailStand>(neuerDetailStand(schritt));
  // Der zuletzt gezeigte Schritt — darauf werden gespeicherte Listen gelegt.
  // Layout-Effekt: steht vor jedem neuen Ereignis schon auf dem neuen Stand.
  const schrittRef = React.useRef(schritt);
  useLayoutEffect(() => {
    schrittRef.current = schritt;
  });

  /**
   * Ergebnis eines Auftrags in Oberfläche und Editor übernehmen — auch beim
   * stillen Schreiben, sonst hielte der Editor (PDF-Export, Versionskopie)
   * einen überholten Stand. Gemeldet wird der Stand der Datenbank nach dem
   * Schreiben (alle Schreibvorgänge des Schritts laufen hintereinander);
   * offene, neuere lokale Fassungen übernehmen ihn nicht.
   */
  const uebernimmErgebnis = (d: DetailStand, ergebnis: DetailSpeicherErgebnis | null) => {
    if (!ergebnis) return;
    const aktuell = schrittRef.current;
    if (standRef.current !== d || aktuell.id !== d.schrittId) {
      onSchrittChange(ergebnis.antwort); // Schritt inzwischen gewechselt
      return;
    }
    setListen(d.lokal);
    setDirty(offeneFelder(d).length > 0);
    onSchrittChange(spaltenUebernehmen(aktuell, ergebnis.zeile));
  };

  /**
   * Schreibt die offenen Listen sofort (über die Warteschlange) und räumt den
   * Timer ab.
   *
   * `live` = der Timer ist regulär abgelaufen und die Oberfläche darf
   * mitreden. Beim stillen Schreiben (Aufräumen, Aufruf durch den Editor)
   * bleiben Meldungen aus, der Fehler wird aber weitergegeben, damit der
   * Aufrufer den Versionswechsel abbrechen kann.
   */
  const flush = async (live: boolean) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    const d = standRef.current;
    if (!offeneFelder(d).length) return;
    try {
      const ergebnis = await speichereDetail(d, DETAIL_DB);
      uebernimmErgebnis(d, ergebnis);
      if (live && standRef.current === d) setMeldung('Zwischenstand automatisch gesichert.');
    } catch (e) {
      console.error(e);
      if (live) setMeldung('Auto-Speichern fehlgeschlagen. Bitte prüfen Sie Ihre Verbindung.', 'fehler');
      else throw e;
    } finally {
      // Ist inzwischen neu geplant, läuft die Anzeige weiter.
      if (live) setSaving(!!saveTimeoutRef.current);
    }
  };

  // Immer die jüngste Fassung von flush(): Aufräumfunktion und Editor rufen
  // über diese Referenz auf und arbeiten so nie mit einer veralteten Closure.
  const flushRef = React.useRef(flush);
  useEffect(() => {
    flushRef.current = flush;
  });

  /** Plant das Autospeichern (neu). Was es schreibt, berechnet der Auftrag erst bei Ausführung. */
  const planeSpeichern = () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    setSaving(true);
    setMeldung('Ungespeicherte Änderungen erfasst...');
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      void flushRef.current(true);
    }, 1200);
  };
  // Für den Abgleich-Effekt: immer die jüngste Fassung.
  const planeSpeichernRef = React.useRef(planeSpeichern);
  useEffect(() => {
    planeSpeichernRef.current = planeSpeichern;
  });

  /** Eine Liste wurde hier geändert: Revision hoch, speichern lassen. */
  const aendere = (feld: keyof Listen, items: string[]) => {
    const d = standRef.current;
    aendereDetail(d, feld, items);
    setListen(d.lokal);
    setDirty(true);
    planeSpeichern();
  };

  useEffect(() => {
    if (standRef.current.schrittId !== schritt.id) standRef.current = neuerDetailStand(schritt);
    setListen(standRef.current.lokal);
    setDirty(false);
    setSaving(false);
    setMeldung(null);
    setKatalogStatus(null);
    // Aufräumen muss SCHREIBEN, nicht verwerfen: sonst verlöre ein
    // Versionswechsel oder das Schließen des Moduls die letzte Änderung.
    return () => { void flushRef.current(false).catch(() => { /* bereits protokolliert */ }); };
  }, [schritt.id]);

  /*
   * Neuer Elternzustand — „Unterlagen ergänzen“, der Bearbeiten-Dialog oder
   * die eigene Rückmeldung (Regeln: `uebernimmVonAussen`):
   *  - Listen ohne offene Änderung übernehmen ihn sofort;
   *  - offene Listen, die außen geändert wurden („außen geändert, lokal
   *    ungespeichert“), bleiben stehen, und es wird gespeichert: der Auftrag
   *    führt dreiwege gegen die FRISCHE Zeile zusammen (der Elternzustand
   *    kann einen Moment älter sein als die Basis und taugt dafür nicht).
   */
  useEffect(() => {
    const d = standRef.current;
    if (d.schrittId !== schrittRef.current.id) return; // Wechsel: der Id-Effekt übernimmt
    const aussen = listenAus({
      schutzmassnahmen: schritt.schutzmassnahmen,
      vorschriften: schritt.vorschriften,
      mitgeltende_dokumente: schritt.mitgeltende_dokumente,
    });
    const speichern = uebernimmVonAussen(d, aussen);
    setListen(d.lokal);
    if (speichern) planeSpeichernRef.current();
  }, [schritt.schutzmassnahmen, schritt.vorschriften, schritt.mitgeltende_dokumente]);

  useEffect(() => {
    onRegisterFlush?.(() => flushRef.current(false));
    return () => onRegisterFlush?.(null);
  }, [onRegisterFlush]);

  const setM = (items: string[]) => aendere('schutzmassnahmen', items);
  const setV = (items: string[]) => aendere('vorschriften', items);
  const setD = (items: string[]) => aendere('mitgeltende_dokumente', items);

  const speichern = async () => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
      saveTimeoutRef.current = null;
    }
    // Ein geplantes Autospeichern ist hiermit abgedeckt: der Auftrag schreibt
    // alle offenen Listen mit ihrem Stand bei Ausführung.
    const d = standRef.current;
    setSaving(true);
    setMeldung(null);
    try {
      const ergebnis = await speichereDetail(d, DETAIL_DB);
      uebernimmErgebnis(d, ergebnis);
      if (!ergebnis) setDirty(offeneFelder(d).length > 0);
      setMeldung('Erfolgreich manuell gespeichert.');
    } catch (e) {
      console.error(e);
      setMeldung('Speichern fehlgeschlagen (Datenbank-Fehler).', 'fehler');
    } finally {
      setSaving(false);
    }
  };

  const kiVorschlag = async () => {
    setMeldung(null);
    const antwort = await suggestHazards(schritt.id);
    if (antwort.status === 'fehler') {
      setMeldung(
        antwort.art === 'netzwerk'
          ? `KI-Vorschlag fehlgeschlagen — der KI-Dienst war nicht erreichbar. Bitte Verbindung prüfen und erneut versuchen. (${antwort.fehler})`
          : `KI-Vorschlag fehlgeschlagen — die Antwort der KI war nicht auswertbar. Bitte erneut versuchen. (${antwort.fehler})`,
        'fehler',
      );
      return;
    }
    const result = antwort.data;
    if (!result?.gefaehrdungen?.length) {
      setMeldung('Die KI hat zu diesem Arbeitsschritt keine Gefährdungen geliefert. Ergänzen Sie Bezeichnung oder Gewerk und versuchen Sie es erneut.', 'fehler');
      return;
    }
    // Auf den Stand NACH der KI-Antwort aufsetzen (inzwischen Übernommenes bleibt).
    const d = standRef.current;
    if (d.schrittId !== schritt.id) return; // inzwischen anderer Schritt
    const neueM = new Set(d.lokal.schutzmassnahmen);
    const neueV = new Set(d.lokal.vorschriften);
    result.gefaehrdungen.forEach((g: any) => {
      (g.schutzmassnahmen || []).forEach((m: any) => {
        if (m?.beschreibung) neueM.add(m.beschreibung);
        if (m?.regelwerkReferenz) neueV.add(m.regelwerkReferenz);
      });
    });
    // Ohne Autospeichern (wie bisher), aber als offen markiert: das nächste
    // Speichern schreibt die KI-Ergebnisse mit, der Abgleich führt sie zusammen.
    aendereDetail(d, 'schutzmassnahmen', Array.from(neueM));
    aendereDetail(d, 'vorschriften', Array.from(neueV));
    setListen(d.lokal);
    setDirty(true);
    setMeldung(`KI: ${result.gefaehrdungen.length} Gefährdungen ausgewertet — Maßnahmen/Vorschriften ergänzt. Speichern nicht vergessen.`);
  };

  // Aktuellen Schritt als globalen Katalog-Eintrag sichern (für alle Nutzer/Projekte)
  const inKatalog = async () => {
    setKatalogStatus(null);
    try {
      const { error } = await supabase.from('sige_arbeiten_katalog').insert({
        bezeichnung: schritt.bezeichnung,
        hauptabschnitt: schritt.hauptabschnitt || null,
        gewerk: schritt.gewerk || null,
        schutzmassnahmen: massnahmen,
        vorschriften,
        mitgeltende_dokumente: dokumente,
        hinweis_code: schritt.hinweis_code || null,
        koordinierungsbedarf: schritt.koordinierungsbedarf || null,
        zustaendig_erstellung: schritt.zustaendig_erstellung || null,
        zustaendig_kontrolle: schritt.zustaendig_kontrolle || null,
        zustaendig_instandhaltung: schritt.zustaendig_instandhaltung || null,
      });
      if (error) throw error;
      setKatalogStatus('Im globalen Katalog gespeichert — steht allen zur Verfügung.');
    } catch (e) {
      console.error(e);
      setKatalogStatus('Katalog-Speichern fehlgeschlagen (Migration v3 eingespielt?).');
    }
  };

  // BG-BAU-Bausteine stehen nicht mehr im Plan, nur noch im Handbuch — sie
  // bleiben sichtbar, aber ausdrücklich so beschriftet und zurückgenommen.
  const bgBausteine = schritt.hinweis_codes?.length
    ? schritt.hinweis_codes.join('; ')
    : (schritt.hinweis_code && schritt.hinweis_code.toLowerCase() !== 'n.v.' ? schritt.hinweis_code : '');

  return (
    <div className="flex-1 overflow-y-auto p-4 space-y-4">
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">{schritt.hauptabschnitt || 'Ohne Abschnitt'}</div>
            <h2 className="font-bold text-slate-800">{schritt.bezeichnung}</h2>
            <div className="text-xs text-slate-500 mt-1">
              {schritt.gewerk && <span className="mr-3">Gewerk: {schritt.gewerk}</span>}
              {schritt.zustaendig_erstellung && <span className="mr-3">Erstellung: {schritt.zustaendig_erstellung}</span>}
              {bgBausteine && (
                <span className="text-slate-400" title="BG-BAU-Bausteine erscheinen nicht im SiGe-Plan, nur im Handbuch.">
                  BG-Baustein (nur Handbuch): {bgBausteine}
                </span>
              )}
            </div>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button data-tutorial="sigeplan.schritt-ki-vorschlag" variant="outline" size="sm" onClick={kiVorschlag} disabled={aiLoading} title="KI schlägt Schutzmaßnahmen + Vorschriften vor">
              {aiLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Bot className="w-4 h-4" />}
              <span className="ml-1 hidden lg:inline">KI-Vorschlag</span>
            </Button>
            <Button variant="outline" size="sm" onClick={inKatalog} title="Diesen Schritt in den globalen Katalog übernehmen (für alle)">
              <BookmarkPlus className="w-4 h-4" />
              <span className="ml-1 hidden lg:inline">In Katalog</span>
            </Button>
          </div>
        </div>
        {(meldung || katalogStatus) && (
          <div className={`mt-3 text-xs border rounded px-2 py-1.5 space-y-0.5 ${
            meldung && meldungTon === 'fehler'
              ? 'text-red-700 bg-red-50 border-red-200'
              : 'text-blue-700 bg-blue-50 border-blue-100'
          }`}>
            {meldung && <div>{meldung}</div>}
            {katalogStatus && <div>{katalogStatus}</div>}
          </div>
        )}
      </div>

      <Liste
        titel="Schutzeinrichtungen / Schutzmaßnahmen (Spalte 2 im Plan)"
        icon={<ShieldCheck className="w-4 h-4 text-green-600" />}
        items={massnahmen}
        placeholder="z.B. 3-teiliger Seitenschutz..."
        onChange={setM}
      />

      <Liste
        titel="Arbeitsvorschriften (Spalte 3 im Plan)"
        icon={<BookOpen className="w-4 h-4 text-blue-600" />}
        items={vorschriften}
        placeholder="z.B. DGUV Vorschrift 38..."
        onChange={setV}
      />

      <MitgeltendeDokumenteEditor
        darstellung="karte"
        titel="Mitgeltende Dokumente (Spalte 3 im Plan, unter den Vorschriften)"
        icon={<FileText className="w-4 h-4 text-indigo-600" />}
        dokumente={dokumente}
        onChange={setD}
        schritt={{
          bezeichnung: schritt.bezeichnung,
          gewerk: schritt.gewerk,
          schutzmassnahmen: massnahmen,
          vorschriften,
        }}
      />

      <div className="sticky bottom-2 space-y-2">
        {saving && (
          <div className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-2.5 py-1.5 flex items-center justify-center gap-2 shadow-sm animate-pulse">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-500" />
            <span>Automatisches Zwischenspeichern...</span>
          </div>
        )}
        {dirty && !saving && (
          <Button variant="primary" className="w-full shadow-lg" onClick={speichern}>
            <Save className="w-4 h-4 mr-2" />
            Änderungen jetzt speichern
          </Button>
        )}
      </div>
    </div>
  );
};
